import { TAXES, formatCompact, formatCount, formatPercent, categoryMeta, displayName } from "./dashboardData";

/**
 * Tutorial da tela Créditos. Os textos dos conceitos seguem
 * documentacao-metodologias/creditos/metodologia-de-calculo.md.
 * `ctx` vem de creditos/index.js: dados carregados e ações da tela.
 */

const topTax = (ctx) => {
  const rows = TAXES.map((t) => ({ label: t.label, value: ctx.kpis[t.kpi] || 0 })).sort((a, b) => b.value - a.value);
  return rows[0];
};
const share = (value, total) => formatPercent(total > 0 ? (value / total) * 100 : 0);
const hasData = (ctx) => ctx.hasData;
const noData = (ctx) => ctx.screen !== "data";

// Passo em que a própria pessoa abre o modal. Abrir não executa nada.
const openAnalysis = (target, title, body) => ({
  id: "open-analysis",
  target,
  noScroll: true,
  title,
  body,
  action: {
    hint: "Clique em Iniciar análise",
    done: (ctx) => ctx.analiseOpen,
    perform: (ctx) => ctx.setAnaliseOpen(true),
  },
});

// Passos dentro do modal, que fica em modo de demonstração durante o tutorial
const inModal = {
  before: (ctx) => ctx.setAnaliseOpen(true),
  after: (ctx) => ctx.setAnaliseOpen(false),
  placement: "right",
  alwaysScroll: true,
};
const analysisSteps = [
  {
    ...inModal,
    id: "modal-period",
    target: '[data-tour="analise-periodo"]',
    title: "Qual período analisar",
    body: "Em branco, a análise cobre todo o período disponível. O início conta do 1º dia do mês, e o fim, até o último.",
  },
  {
    ...inModal,
    id: "modal-companies",
    target: '[data-tour="analise-empresas"]',
    title: "Quais empresas entram",
    body: "Sem seleção, entram todas as empresas do projeto. Marque algumas só se quiser um recorte.",
  },
  {
    ...inModal,
    id: "modal-taxes",
    target: '[data-tour="analise-tipos"]',
    title: "Quais impostos calcular",
    body: "São 12 metodologias: 5 de ICMS, 2 de IPI e 5 de PIS/COFINS. Desligue um imposto para deixar as metodologias dele de fora.",
  },
  {
    ...inModal,
    id: "modal-params",
    target: '[data-tour="analise-parametros"]',
    title: "Alíquota de importados",
    body: "Vale para operações interestaduais com produtos importados. O padrão legal é 4%, então só altere se houver motivo.",
  },
  {
    ...inModal,
    id: "modal-confirm",
    target: '[data-tour="analise-confirmar"]',
    placement: "top",
    title: "Confira e inicie",
    body: (ctx) => (ctx.hasData
      ? "O resumo mostra o que será analisado. Ao iniciar, tudo é recalculado do zero e os créditos atuais são substituídos. Neste tutorial, nada é executado."
      : "O resumo mostra o que será analisado. Ao iniciar, o progresso aparece aqui mesmo, rotina por rotina. Neste tutorial, nada é executado."),
    insight: (ctx) => (ctx.hasData ? `Os ${formatCompact(ctx.kpis.totalCreditos)} atuais seriam substituídos pelo novo resultado.` : null),
  },
];

const creditosTour = {
  id: "creditos",
  version: 2,
  title: "Guia de Créditos",
  intro: "Aprenda a ler a tela no seu ritmo. Cada tutorial usa os dados do seu projeto.",
  // Explica no guia por que há tutoriais bloqueados
  notice: (ctx) => {
    if (ctx.screen === "no-project") return "Selecione um projeto na barra de status para ver os tutoriais. O glossário continua disponível.";
    if (ctx.screen === "error") return "Os créditos não carregaram, e os tutoriais dependem deles. Use Tentar novamente na tela. O glossário continua disponível.";
    if (ctx.screen === "loading") return "Carregando os dados do projeto. Os tutoriais abrem em seguida.";
    if (ctx.screen === "empty") return "Os outros tutoriais usam os dados do projeto e abrem depois da primeira análise.";
    if (ctx.screen === "filtered") return "Os filtros atuais não têm créditos. Limpe os filtros para abrir os outros tutoriais.";
    if (ctx.supplierCount === 0) return ctx.suppliersLoading ? "Carregando os fornecedores. O tutorial deles abre em seguida." : "Não há fornecedores para mostrar, então o tutorial deles fica bloqueado.";
    return null;
  },
  chapters: [
    {
      key: "first-analysis",
      when: (ctx) => ctx.screen === "empty",
      title: "Primeira análise",
      summary: "Por que está vazio e como começar",
      minutes: "1 min",
      offer: { title: "Ainda sem créditos aqui", text: "Em 1 minuto, mostramos como rodar a primeira análise. Nada é executado no tutorial." },
      steps: [
        {
          id: "why-empty",
          target: '[data-tour="empty"]',
          title: "Por que a tela está vazia",
          body: "Os créditos só aparecem depois de uma análise. Ela percorre, item por item, os SPEDs já processados do projeto e aplica as metodologias de cada imposto.",
        },
        openAnalysis('[data-tour="empty-start"]', "Abra a análise", "Antes de rodar, você escolhe o que entra. Abrir não inicia nada."),
        ...analysisSteps,
        {
          id: "after-analysis",
          title: "Depois da análise",
          body: "A tela se preenche com o total, os impostos, os fornecedores e os tipos de crédito, e os outros tutoriais são liberados. Se nada aparecer, confira se os SPEDs do período já foram processados.",
        },
      ],
    },
    {
      key: "no-results",
      when: (ctx) => ctx.screen === "filtered",
      title: "Sem resultados",
      summary: "Por que os filtros esconderam tudo",
      minutes: "15 s",
      steps: [
        {
          id: "why-filtered",
          target: '[data-tour="empty"]',
          title: "Nada neste recorte",
          body: "Não há créditos para o período ou a empresa escolhidos. Isso não quer dizer que o projeto esteja sem créditos.",
        },
        {
          id: "clear-filters",
          target: '[data-tour="empty-clear"]',
          noScroll: true,
          title: "Volte ao projeto inteiro",
          body: "Limpar os filtros traz de volta tudo o que foi encontrado no projeto.",
          action: {
            hint: "Clique em Limpar filtros",
            done: (ctx) => !ctx.hasFilters,
            perform: (ctx) => ctx.clearFilters(),
          },
        },
      ],
    },
    {
      key: "overview",
      locked: noData,
      offer: { title: "Novo em Créditos?", text: "Em 1 minuto, mostramos como ler esta tela com os dados do seu projeto." },
      title: "Visão geral",
      summary: "Quanto há para recuperar e de onde vem",
      minutes: "1 min",
      steps: [
        {
          id: "total",
          when: hasData,
          before: (ctx) => ctx.setTab("overview"),
          target: '[data-tour="kpi-total"]',
          title: "Quanto foi identificado",
          body: "Este é o total de oportunidades de crédito encontradas nos SPEDs do projeto. Clicar no card abre a lista completa.",
          insight: (ctx) => `Seu projeto tem ${formatCompact(ctx.kpis.totalCreditos)} em ${formatCount(ctx.itemCount)} itens.`,
        },
        {
          id: "taxes",
          when: hasData,
          target: '[data-tour="kpi-tax"]',
          title: "Separado por imposto",
          body: "Cada card mostra um imposto, e a linha mostra como ele variou no tempo. Clicar filtra a lista por aquele imposto.",
          insight: (ctx) => { const t = topTax(ctx); return `${t.label} é o maior: ${share(t.value, ctx.kpis.totalCreditos)} do total.`; },
        },
        {
          id: "trend",
          when: (ctx) => ctx.hasData && ctx.months.length > 0,
          target: '[data-tour="trend"]',
          title: "Em que mês o crédito aparece",
          body: "O gráfico mostra o crédito por mês de emissão do documento, com uma linha para cada imposto. Picos costumam merecer uma conferência primeiro.",
          insight: (ctx) => {
            const peak = ctx.months.reduce((best, m) => (!best || m.total > best.total ? m : best), null);
            return peak ? `Seu maior mês foi ${ctx.formatMonth(peak.key)}, com ${formatCompact(peak.total)}.` : null;
          },
        },
        {
          id: "map",
          when: (ctx) => ctx.hasData && ctx.months.length > 0,
          target: '[data-tour="period-map"]',
          title: "Filtre clicando em um mês",
          body: "Quanto mais forte o verde, maior o crédito do mês. Clicar em um mês aplica o filtro em toda a tela, e o seletor no alto volta ao período completo.",
        },
        {
          id: "categories",
          when: (ctx) => ctx.hasData && ctx.categories.length > 0,
          target: '[data-tour="categories"]',
          title: "De onde vem o crédito",
          body: "O tamanho de cada bloco acompanha a participação da categoria. Clique em um deles para ver só os créditos daquela categoria.",
          insight: (ctx) => { const c = ctx.categories[0]; const t = ctx.categories.reduce((s, x) => s + x.total, 0); return `${categoryMeta(c.key).label} lidera, com ${share(c.total, t)}.`; },
        },
        {
          id: "rankings",
          when: hasData,
          target: '[data-tour="rankings"]',
          title: "Por onde começar a revisar",
          body: "Aqui estão os fornecedores e as oportunidades que mais pesam. Revisar estes primeiro traz o maior retorno com menos trabalho.",
          insight: (ctx) => {
            const top = ctx.topSupplier;
            return top ? `${displayName(top.name)} é o maior fornecedor, com ${formatCompact(top.total)}.` : null;
          },
        },
      ],
    },
    {
      key: "suppliers",
      locked: (ctx) => noData(ctx) || ctx.supplierCount === 0,
      title: "Fornecedores",
      summary: "De quem depende o crédito",
      minutes: "1 min",
      steps: [
        {
          id: "go-suppliers",
          when: hasData,
          before: (ctx) => { if (ctx.tab === "suppliers") ctx.setTab("overview"); },
          target: '[data-key="suppliers"]',
          noScroll: true,
          title: "Abra a aba Fornecedores",
          body: "Cada aba responde a uma pergunta. Esta mostra de quem depende o crédito.",
          action: {
            hint: "Clique em Fornecedores",
            done: (ctx) => ctx.tab === "suppliers",
            perform: (ctx) => ctx.setTab("suppliers"),
          },
        },
        {
          id: "sup-kpis",
          when: hasData,
          target: '[data-tour="sup-kpis"]',
          title: "Quantos fornecedores importam",
          body: "Normalmente, poucos fornecedores concentram a maior parte do crédito. O segundo card mostra quantos somam 80% do total.",
          insight: (ctx) => (ctx.supplierCount > 0 ? `São ${formatCount(ctx.supplierCount)} fornecedores neste projeto.` : null),
        },
        {
          id: "abc",
          when: hasData,
          target: '[data-tour="abc"]',
          title: "Curva ABC",
          body: "A classe A soma até 80% do crédito, a B vai de 80% a 95%, e a C fica com os últimos 5%. Clique em uma classe para filtrar a lista.",
        },
        {
          id: "directory",
          when: hasData,
          target: '[data-tour="sup-directory"]',
          placement: "top",
          title: "Todos os fornecedores",
          body: "Busque por nome ou CNPJ. Clicar em um fornecedor abre uma tela só dele, com documentos e itens.",
        },
      ],
    },
    {
      key: "credits",
      locked: noData,
      title: "Tipos de crédito",
      summary: "O que compõe cada oportunidade",
      minutes: "1 min",
      steps: [
        {
          id: "go-credits",
          when: hasData,
          before: (ctx) => { if (ctx.tab === "credits") ctx.setTab("overview"); },
          target: '[data-key="credits"]',
          noScroll: true,
          title: "Abra Tipos de crédito",
          body: "Aqui ficam as oportunidades, cada uma com a sua metodologia.",
          action: {
            hint: "Clique em Tipos de crédito",
            done: (ctx) => ctx.tab === "credits",
            perform: (ctx) => ctx.setTab("credits"),
          },
        },
        {
          id: "credit-filters",
          when: hasData,
          target: '[data-tour="credit-filters"]',
          title: "Encontre o que procura",
          body: "Combine a busca com os filtros de imposto, categoria e ordenação. Os filtros usados pelos gráficos também aparecem aqui.",
        },
        {
          id: "credit-formula",
          when: hasData,
          target: '[data-tour="credit-grid"]',
          placement: "top",
          title: "Cada card é uma oportunidade",
          body: "O valor é sempre a base do SPED vezes a alíquota. Abra um card para ver a metodologia, a base legal e o que vale conferir antes de usar o crédito.",
        },
      ],
    },
    {
      key: "actions",
      locked: noData,
      title: "Filtros e análise",
      summary: "Período e como rodar uma nova análise",
      minutes: "1 min",
      steps: [
        {
          id: "period",
          target: '[data-tour="period"]',
          noScroll: true,
          title: "Escolha o período",
          body: "O período filtra a tela inteira, mas alguns rankings por fornecedor são de todo o projeto. Quando isso acontece, a tela avisa.",
        },
        openAnalysis('[data-tour="analise"]', "Nova análise", "Vale rodar depois de processar novos SPEDs. Abrir não inicia nada: antes, você escolhe o que entra."),
        ...analysisSteps,
        {
          id: "help",
          target: '[data-tour="help"]',
          noScroll: true,
          title: "A ajuda fica aqui",
          body: "Volte a este guia quando quiser para rever um tutorial ou consultar o glossário dos termos.",
        },
      ],
    },
  ],
  glossary: [
    { term: "Crédito identificado", text: "Oportunidade encontrada pelo sistema nos SPEDs do projeto. O valor é a base de cálculo vezes a alíquota, dividido por 100. Parte das regras usa palavras na descrição do item, então vale conferir antes de usar." },
    { term: "Base de cálculo", text: "Valor do item ou do documento no próprio SPED, sobre o qual a alíquota é aplicada." },
    { term: "Ativo Imobilizado (CIAP)", text: "Bens do ativo comprados com ICMS destacado que não foram escriturados no bloco CIAP do SPED. O crédito de ICMS é apropriado em 48 parcelas mensais." },
    { term: "Insumos sem crédito", text: "Compras de insumos ou mercadorias para revenda em que o imposto não foi aproveitado." },
    { term: "Uso e Consumo", text: "Compras classificadas como uso e consumo que poderiam ser reclassificadas como produto intermediário, embalagem ou combustível e lubrificante, categorias com jurisprudência favorável ao crédito." },
    { term: "Frete", text: "Documentos de transporte (CT-e) em que o imposto não foi aproveitado." },
    { term: "Energia Elétrica", text: "Contas de energia em que o crédito não foi aproveitado. No ICMS depende de laudo técnico do uso produtivo: sem laudo cadastrado, o sistema assume 100% e sinaliza que o laudo é necessário." },
    { term: "Fornecedor do Simples Nacional", text: "Algumas metodologias excluem esses fornecedores. O sistema identifica pelo CST do item no SPED, sem consultar um cadastro oficial. Item sem CST é mantido como oportunidade." },
    { term: "Curva ABC", text: "Classifica os fornecedores pelo crédito acumulado: a classe A soma até 80%, a B vai de 80% a 95%, e a C fica com os últimos 5%." },
    { term: "Nova análise", text: "Recalcula tudo do zero sobre os SPEDs processados e substitui os créditos anteriores do projeto." },
  ],
};

export default creditosTour;
